import React from "react";
import { Facebook, ExternalLink, Sparkles } from "lucide-react";

/**
 * Facebook Page Plugin — embeds the college's live Facebook feed.
 * Uses the official Meta iframe plugin — zero API permissions needed.
 * Auto-refreshes as new posts are published on Facebook.
 */
const FB_PAGE_ID = "441525065717901";
const FB_PAGE_URL = `https://www.facebook.com/${FB_PAGE_ID}`;

// Build the plugin iframe URL. `tabs=timeline` = show posts feed.
const PLUGIN_URL = new URL("https://www.facebook.com/plugins/page.php");
PLUGIN_URL.searchParams.set("href", FB_PAGE_URL);
PLUGIN_URL.searchParams.set("tabs", "timeline");
PLUGIN_URL.searchParams.set("width", "500");
PLUGIN_URL.searchParams.set("height", "700");
PLUGIN_URL.searchParams.set("small_header", "false");
PLUGIN_URL.searchParams.set("adapt_container_width", "true");
PLUGIN_URL.searchParams.set("hide_cover", "false");
PLUGIN_URL.searchParams.set("show_facepile", "true");

export default function FacebookFeed({ lang }) {
    const isAr = lang === "ar";

    return (
        <section className="mb-16" data-testid="facebook-feed-section">
            {/* Header */}
            <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#1877F2]/10 border border-[#1877F2]/30 text-[#0864D3] text-[11px] font-bold uppercase tracking-[0.2em] mb-3">
                    <Facebook size={13} />
                    {isAr ? "آخر أخبارنا" : "מה חדש אצלנו"}
                </div>
                <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#0B1F3D]">
                    {isAr ? "تابعنا على فيسبوك" : "עקבו אחרינו בפייסבוק"}
                </h2>
                <p className="text-[#475569] mt-2 max-w-xl mx-auto text-sm">
                    {isAr
                        ? "منشورات، فعاليات، قصص نجاح وكل الأخبار — كل شيء يظهر هنا مباشرة."
                        : "פוסטים, אירועים, סיפורי הצלחה וכל החדשות — הכל מופיע כאן אוטומטית."}
                </p>
            </div>

            {/* 2-column layout: iframe (left) + CTA card (right) */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
                {/* Facebook Page Plugin iframe */}
                <div className="lg:col-span-3">
                    <div className="bg-white rounded-2xl border border-[#F1E4D2] shadow-[0_10px_30px_rgba(11,31,61,0.08)] overflow-hidden">
                        <iframe
                            src={PLUGIN_URL.toString()}
                            width="500"
                            height="700"
                            style={{ border: "none", overflow: "hidden", width: "100%" }}
                            scrolling="no"
                            frameBorder="0"
                            allowFullScreen={true}
                            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                            title="Al-Ahliyya College Facebook Feed"
                            data-testid="facebook-feed-iframe"
                        />
                    </div>
                </div>

                {/* CTA sidebar */}
                <div className="lg:col-span-2">
                    <div
                        className="relative rounded-2xl p-8 overflow-hidden border border-[#1A365D]/30 h-full min-h-[300px] flex flex-col justify-between"
                        style={{
                            background: "radial-gradient(ellipse at 20% 20%, rgba(24,119,242,0.3) 0%, transparent 55%), linear-gradient(135deg, #0B1F3D 0%, #1A365D 100%)",
                        }}
                    >
                        <div
                            className="absolute inset-0 opacity-[0.1]"
                            style={{
                                backgroundImage: "linear-gradient(rgba(212,175,55,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.5) 1px, transparent 1px)",
                                backgroundSize: "44px 44px",
                            }}
                        />
                        <div className="relative z-10">
                            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#1877F2] to-[#0864D3] flex items-center justify-center mb-4 shadow-[0_10px_30px_rgba(24,119,242,0.5)]">
                                <Facebook size={26} className="text-white" />
                            </div>
                            <h3 className="font-serif text-2xl font-bold text-white leading-tight mb-3">
                                {isAr ? "انضم لمجتمعنا الآن!" : "הצטרפו לקהילה שלנו!"}
                            </h3>
                            <p className="text-white/80 text-sm leading-relaxed mb-5">
                                {isAr
                                    ? "أكثر من 2,200 طالب وخرّيج يتابعوننا. تحديثات يومية، فعاليات، منح دراسية وقصص من الحرم الجامعي."
                                    : "יותר מ-2,200 סטודנטים ובוגרים עוקבים אחרינו. עדכונים יומיים, אירועים, מלגות וסיפורים מהקמפוס."}
                            </p>
                            <ul className="space-y-2 text-white/90 text-sm mb-6">
                                <li className="flex items-center gap-2">
                                    <Sparkles size={13} className="text-[#F8D89C] shrink-0" />
                                    {isAr ? "إعلانات فتح التسجيل" : "הודעות על פתיחת רישום"}
                                </li>
                                <li className="flex items-center gap-2">
                                    <Sparkles size={13} className="text-[#F8D89C] shrink-0" />
                                    {isAr ? "قصص نجاح خرّيجينا" : "סיפורי הצלחה של בוגרים"}
                                </li>
                                <li className="flex items-center gap-2">
                                    <Sparkles size={13} className="text-[#F8D89C] shrink-0" />
                                    {isAr ? "معلومات عن المنح والفعاليات" : "מלגות ואירועים"}
                                </li>
                            </ul>
                        </div>

                        <a
                            href={FB_PAGE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="facebook-follow-cta"
                            className="relative z-10 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#1877F2] to-[#0864D3] hover:from-[#0864D3] hover:to-[#1877F2] text-white px-5 py-3 rounded-lg font-bold text-sm shadow-[0_10px_30px_rgba(24,119,242,0.45)] transition-all hover:scale-[1.02]"
                        >
                            <Facebook size={16} />
                            {isAr ? "افتح صفحتنا على فيسبوك" : "לעמוד הפייסבוק שלנו"}
                            <ExternalLink size={14} />
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
}
